import { ComponentPropsWithoutRef, ReactNode } from "react";
import messages from "messages";
// components
import ModalLayout, {
  ModalLayoutProps,
} from "components/modal/modal-layout/ModalLayout";
import { OrchidProGroupSessionJoinButton } from "components/Dashboard/DoctorDashboard/components/orchid-pro-join-button";
import getGroupSessionUrl from "components/Dashboard/utility/getGroupSessionUrl";
import PatientAvatar from "components/timeslot-popover/components/patient-avatar";
import SessionModalHeader from "components/timeslot-popover/components/session-modal-header";
import RoundedImageWithFallback from "components/rounded-image-with-fallback";
import ShowMore from "components/show-more";
// styles
import styles from "./GroupSessionModal.module.scss";
// hooks
import { useCalendarTime } from "components/Dashboard/DoctorDashboard/views/one-one-sessions/contexts/CalendarTimeContext";
import { useOrchidPro } from "components/Dashboard/DoctorDashboard/contexts/orchid-pro-context";
// utils
import getGroupSessionInfo from "./utils/getGroupSessionInfo";
import { apiHostAndPath } from "utils/api";
import { getOrchidProNameAndMedicalDegreeString } from "utils/formatter/orchidPro";
import getClasses from "utils/getClasses";

type Props = ModalLayoutProps & {
  Header?: ReactNode;
  groupSession: GroupSessionMetaData;
  SessionHeader: ReactNode;
} & Pick<
    ComponentPropsWithoutRef<typeof SessionModalHeader>,
    "Title" | "Subtitle"
  >;

const GroupSessionModal = ({
  groupSession,
  Title,
  Subtitle,
  SessionHeader,
  Header,
  ...props
}: Props) => {
  const { orchidPro } = useOrchidPro();
  const { currentCalendarTimezone } = useCalendarTime();

  return !groupSession ? null : (
    <ModalLayout
      size={"lg"}
      contentClassName={styles["group-session-modal__content"]}
      bodyClass={styles["group-session-modal__body-container"]}
      footerClass={styles["group-session-modal__footer"]}
      {...props}
    >
      <div className={styles["group-session-modal__header"]}>{Header}</div>
      <div className={styles["group-session-modal__body"]}>
        {/* TODO: needs the statusnd send reminder feature a */}
        {/* <BodyEntry
          Icon={<OutlineDocumentClean />}
          title={"Intake form required"}
          subtitle={""}
          Action={
            <Link to={""} target="_blank" rel="noopener noreferrer">
              {"Send reminder"}
            </Link>
          }
        /> */}
        <div className={styles["group-session-modal__left-col"]}>
          <div className={styles["group-session-modal__session"]}>
            {SessionHeader}
          </div>
          <div className={styles["group-session-modal__session-info-list"]}>
            <div className={styles["group-session-modal__session-info"]}>
              <label className={styles["group-session-modal__label"]}>
                {messages.sessionDescription}
              </label>
              <ShowMore content={groupSession.description} lines={2} />
            </div>

            <div className={styles["group-session-modal__session-info"]}>
              <div className={styles["group-session-modal__session-details"]}>
                {getGroupSessionInfo(groupSession, currentCalendarTimezone)}
              </div>
              {groupSession.zoom_meeting?.join_link ? (
                <div className={styles["group-session-modal__meeting-link"]}>
                  <div>{"Join Zoom Meeting"}</div>
                  <a
                    href={getGroupSessionUrl(groupSession)}
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    {getGroupSessionUrl(groupSession)}
                  </a>
                  <div className="mt-3 d-flex align-items-center">
                    {"If you are the host, "}
                    <OrchidProGroupSessionJoinButton
                      groupSession={groupSession}
                      variant={"link"}
                      className={styles["group-session-modal__start-zoom"]}
                      title={"Start Zoom Meeting"}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}

              <div>
                {groupSession.zoom_meeting?.meeting_id
                  ? `Meeting ID: ${groupSession.zoom_meeting?.meeting_id}`
                  : ""}
              </div>

              <div>
                {groupSession.zoom_meeting?.password
                  ? `Passcode: ${groupSession.zoom_meeting?.password}`
                  : ""}
              </div>
            </div>
          </div>
        </div>

        <div className={styles["group-session-modal__right-col"]}>
          <div className={styles["group-session-modal__list-group"]}>
            <label
              id={"group-session-speakers"}
              className={styles["group-session-modal__label"]}
            >
              {messages.speakers}
            </label>
            <ul
              aria-labelledby={"group-session-speakers"}
              className={styles["group-session-modal__list"]}
            >
              {groupSession.speakers.map((el) => {
                const isHost =
                  el.cognito_sub === orchidPro?.cognito_sub &&
                  groupSession.is_host;

                return (
                  <li
                    key={el.cognito_sub}
                    className={getClasses(
                      styles["group-session-modal__item"],
                      isHost && styles["group-session-modal__item--host"],
                    )}
                  >
                    <div>
                      {el.cognito_sub && (
                        <RoundedImageWithFallback
                          src={apiHostAndPath(
                            `/api/users/orchid_pro/profile_image/${el.cognito_sub}`,
                          )}
                          width={40}
                          height={40}
                        />
                      )}
                    </div>

                    <div className={styles["group-session-modal__info"]}>
                      <div className={styles["group-session-modal__info-name"]}>
                        {getOrchidProNameAndMedicalDegreeString(el)}
                      </div>
                      {isHost && (
                        <div
                          className={styles["group-session-modal__info-label"]}
                        >
                          {messages.organizer}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className={styles["group-session-modal__list-group"]}>
            <label
              id={"group-session-participants"}
              className={styles["group-session-modal__label"]}
            >
              {messages.participants}
            </label>
            <ul
              aria-labelledby={"group-session-participants"}
              className={styles["group-session-modal__list"]}
            >
              {(groupSession.patients || []).map((el, i) => (
                <li key={i} className={styles["group-session-modal__item"]}>
                  <PatientAvatar />
                  <span className={styles["group-session-modal__info"]}>
                    {[el.first_name, el.last_name].filter((e) => e).join(" ")}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </ModalLayout>
  );
};

export default GroupSessionModal;
